// Home.jsx
import { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiVolumeHigh, mdiVolumeOff } from "@mdi/js";

import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Services from "./components/Services";
import Team from "./components/Team";
import Testimonials from "./components/Testimonials";
import CalendlyInline from "./components/CalendlyInline";
import ContactForm from "./components/ContactForm";
import Footer from "./components/Footer";
import members from "./data/members";

export default function Home() {
  const [muted, setMuted] = useState(true);
  const [audioRef, setAudioRef] = useState(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const audio = new Audio("/ocean.mp3");
    audio.loop = true;
    audio.muted = true;
    audio.volume = 0.25;
    setAudioRef(audio);

    const resumeAudio = () => {
      audio.play().catch(() => {});
      window.removeEventListener("click", resumeAudio);
      window.removeEventListener("touchstart", resumeAudio);
    };

    window.addEventListener("click", resumeAudio);
    window.addEventListener("touchstart", resumeAudio);

    return () => {
      audio.pause();
      window.removeEventListener("click", resumeAudio);
      window.removeEventListener("touchstart", resumeAudio);
    };
  }, []);

  useEffect(() => {
    if (audioRef) audioRef.muted = muted;
  }, [muted, audioRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVideoLoaded(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );

    const current = sectionRef.current;
    if (current) observer.observe(current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950 to-slate-900 text-white">
      <Navbar />

      <section ref={sectionRef} id="home" className="relative min-h-[92dvh] overflow-hidden">
        {!isVideoLoaded && (
          <img
            src="/beach-sunset-preview.jpg"
            alt="Beach preview"
            className="absolute inset-0 z-0 h-full w-full object-cover transition-opacity duration-700"
          />
        )}

        {isVideoLoaded && (
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 z-0 h-full w-full object-cover transition-opacity duration-700"
            poster="/beach-sunset-preview.jpg"
          >
            <source src="/beach-sunset-loop.mp4" type="video/mp4" />
          </video>
        )}

        <div className="absolute inset-0 z-10 bg-gradient-to-b from-slate-950/70 via-blue-950/35 to-slate-950" />
        <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_center,rgba(103,232,249,0.18),transparent_45%)]" />

        <div className="relative z-20 mx-auto flex min-h-[92dvh] max-w-7xl flex-col items-center justify-center px-6 text-center">
          <img
            src="/logo.png"
            alt="Lion Pride Financial"
            className="mb-8 w-64 object-contain drop-shadow-2xl md:w-[28rem]"
          />
          <div className="max-w-3xl rounded-3xl border border-white/10 bg-slate-950/45 p-6 shadow-2xl backdrop-blur md:p-8">
            {/*<p className="mb-4 text-sm font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Lion Pride Financial
            </p>*/}
            <h1 className="text-4xl font-bold leading-tight md:text-6xl">
              Confidence in a Sea of Uncertainty.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-slate-200 md:text-xl">
              Bringing calm, structure, and strategic clarity to complex financial decisions.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <a href="#contact" className="rounded-full bg-cyan-500 px-6 py-3 font-bold text-white shadow-lg shadow-cyan-950/40 transition hover:bg-cyan-400">
                Start a Conversation
              </a>
              <a href="/publications" className="rounded-full border border-white/20 bg-white/10 px-6 py-3 font-bold text-white transition hover:bg-white/15">
                View Publications
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 z-30 -translate-x-1/2 group">
          <button
            type="button"
            onClick={() => {
              if (audioRef) {
                const nextMuted = !muted;
                audioRef.muted = nextMuted;
                setMuted(nextMuted);
                localStorage.setItem("muted", String(nextMuted));
              }
            }}
            className="flex items-center justify-center rounded-full border border-white/15 bg-black/50 px-4 py-2 text-white transition hover:bg-black/70"
            aria-label={muted ? "Unmute sound" : "Mute sound"}
          >
            <Icon path={muted ? mdiVolumeOff : mdiVolumeHigh} size={1} color="#FFFFFF" />
          </button>
          <div className="absolute bottom-full mb-2 rounded bg-black/75 px-3 py-1 text-xs text-white opacity-0 transition group-hover:opacity-100">
            {muted ? "Sound Off" : "Sound On"}
          </div>
        </div>
      </section>

      <main className="relative z-20 bg-gradient-to-b from-slate-950 via-blue-950 to-slate-900">
        <Hero />
        <Services />
        <Team members={members} />
        <Testimonials />
        <CalendlyInline />
        <ContactForm />
      </main>

      <Footer />
    </div>
  );
}
